import { CommonModule } from '@angular/common';
import { HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgModule, ErrorHandler } from '@angular/core';
import { BACKEND_API_URL, CamelCaseInterceptor, OrganizationSiteInterceptor, SpaqFireDb, ThrottleInterceptor } from '@shared/core';
import {
	APP_INSIGHTS_CONFIG,
	CONTRACT_CONDITIONS_TEXT,
	RECAPTCHA_CONFIG,
	SERVICE_MODEL,
	ScopeInterceptor,
	setScopeConfig,
	setServicesConfig,
	SpaqErrorHandler
} from '@shared/store';
import { BrowserModule } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { CoreGuardModule } from '@spaq/core/core-guard.module';
import { CoreLayoutModule } from '@spaq/core/core-layout.module';
import { CoreServicesModule } from '@spaq/core/core-services.module';
import { PipesModule } from '@spaq/shared/pipes.module';
import { SharedComponentsModule } from '@spaq/shared/shared-components.module';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { SpaqModule } from '@spaq/lib/spaq.module';
import { DialogModule, DialogRef } from '@progress/kendo-angular-dialog';
import { IntlModule } from '@progress/kendo-angular-intl';
import { AppComponent } from './app.component';
import {
	contractPaymentCreditCard,
	contractPaymentDas,
	contractPaymentInternet,
	contractPaymentPpa,
	contractService
} from './configs/contract-text';
import { environment } from './configs/environment';
import { Service } from '@spaq/utils/service-model';
import { EffectsModule } from './effects.module';
import { StoreModule } from './store.module';
import { StoresModule } from './stores.module';
import { WebModule } from './web/web.module';
import { NgxGoogleAnalyticsModule, NgxGoogleAnalyticsRouterModule } from 'ngx-google-analytics';
import { NgxMdModule } from 'ngx-md';
import { DebugModule } from '@spaq/web/debug.module';

// Reducers have no DI access; give the shared @shared/store layout reducer this app's
// scoped config before the store can dispatch into it.
setScopeConfig(environment.scopes);
setServicesConfig(environment.services);

export class FirebaseTransLoader implements TranslateLoader {
	constructor(private db: SpaqFireDb) {}

	public getTranslation(lang: string, prefix = 'language/'): any {
		return this.db.translations$(lang, prefix);
	}
}

export function HttpLoaderFactory(http: HttpClient) {
	return new TranslateHttpLoader(http, '/assets/i18n/');
}

export function FbTransLoaderFactory(db: SpaqFireDb) {
	return new FirebaseTransLoader(db);
}

@NgModule({
	declarations: [AppComponent],
	imports: [
		NgxMdModule.forRoot(),
		CommonModule,
		CoreServicesModule,
		CoreLayoutModule,
		SharedComponentsModule,
		BrowserModule,
		CoreGuardModule,
		PipesModule,
		EffectsModule,
		StoreModule,
		DialogModule,
		IntlModule,
		DebugModule,
		StoresModule,
		SpaqModule.forRoot({
			firebase: {
				config: environment.firebase
			},
			locale: 'fr-CA'
		}),
		WebModule,
		TranslateModule.forRoot({
			loader: {
				provide: TranslateLoader,
				useFactory: FbTransLoaderFactory,
				deps: [SpaqFireDb]
			}
		}),
		NgxGoogleAnalyticsModule.forRoot('G-J19C6V2P5T'),
		NgxGoogleAnalyticsRouterModule,
		RouterModule
	],
	providers: [
		DialogRef,
		{ provide: BACKEND_API_URL, useValue: environment.backendApiUrl },
		{ provide: RECAPTCHA_CONFIG, useValue: { disabled: environment.recaptcha?.disabled === true } },
		{
			provide: APP_INSIGHTS_CONFIG,
			useValue: {
				connectionString: environment.applicationInsights.connectionString,
				enabled: environment.applicationInsights.enabled,
				environmentId: environment.id,
				cloudRole: 'spaq-abonnement-frontend'
			}
		},
		{ provide: SERVICE_MODEL, useValue: Service },
		{
			provide: CONTRACT_CONDITIONS_TEXT,
			useValue: {
				paymentCreditCard: contractPaymentCreditCard,
				paymentDas: contractPaymentDas,
				paymentInternet: contractPaymentInternet,
				paymentPpa: contractPaymentPpa,
				service: contractService
			}
		},
		{ provide: ErrorHandler, useClass: SpaqErrorHandler },
		{ provide: HTTP_INTERCEPTORS, useClass: CamelCaseInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: ScopeInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: ThrottleInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: OrganizationSiteInterceptor, multi: true }
	],
	bootstrap: [AppComponent]
})
export class AppModule {
	constructor() {}
}
